"use client"

import { useState } from "react"
import "./VoteRecoveryModal.css"

const VoteRecoveryModal = ({ isOpen, onClose, onRecoverVotes, allVotes, places, isLoading }) => {
  const [step, setStep] = useState(1)
  const [nickname, setNickname] = useState("")
  const [selectedPlace, setSelectedPlace] = useState("")
  const [error, setError] = useState("")

  if (!isOpen) return null

  // Find all nicknames that have voted
  const getAvailableNicknames = () => {
    const nicknames = new Set()
    Object.values(allVotes).forEach((voteData) => {
      if (voteData.userDetails) {
        voteData.userDetails.forEach((detail) => {
          if (detail.nickname) {
            nicknames.add(detail.nickname)
          }
        })
      }
    })
    return Array.from(nicknames).sort()
  }

  // Get places that this nickname voted on
  const getPlacesVotedByNickname = (targetNickname) => {
    const votedPlaces = []
    Object.entries(allVotes).forEach(([placeKey, voteData]) => {
      if (voteData.userDetails) {
        const userVote = voteData.userDetails.find((detail) => detail.nickname === targetNickname)
        if (userVote) {
          const place = places.find((p) => `${p.lat},${p.lng}` === placeKey)
          if (place) {
            votedPlaces.push({
              ...place,
              vote: userVote.vote,
              votedAt: userVote.votedAt,
            })
          }
        }
      }
    })
    return votedPlaces.sort((a, b) => new Date(b.votedAt) - new Date(a.votedAt))
  }

  const handleNicknameSubmit = () => {
    if (!nickname.trim()) {
      setError("Please enter a nickname")
      return
    }

    const availableNicknames = getAvailableNicknames()
    if (!availableNicknames.includes(nickname.trim())) {
      setError("No votes found for this nickname")
      return
    }

    setError("")
    setStep(2)
  }

  const handleVerification = () => {
    if (!selectedPlace) {
      setError("Please select a place to verify")
      return
    }

    const votedPlaces = getPlacesVotedByNickname(nickname)
    const isValid = votedPlaces.some((place) => place.id === selectedPlace)

    if (!isValid) {
      setError("This doesn't match our records. Please try a different place.")
      return
    }

    // Success! Recover the votes
    onRecoverVotes(nickname)
  }

  const handleClose = () => {
    setStep(1)
    setNickname("")
    setSelectedPlace("")
    setError("")
    onClose()
  }

  const availableNicknames = getAvailableNicknames()
  const votedPlaces = step === 2 ? getPlacesVotedByNickname(nickname) : []

  return (
    <div className="modal-overlay">
      <div className="modal-content vote-recovery-modal">
        <div className="modal-header">
          <h2>🔄 Reclaim Your Previous Votes</h2>
          <button onClick={handleClose} className="modal-close">
            ×
          </button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <div className="recovery-step">
              <p className="step-description">
                Enter the nickname you used when voting before. This will help us find your previous votes.
              </p>

              <div className="form-group">
                <label htmlFor="nickname">Your Previous Nickname:</label>
                <input
                  type="text"
                  id="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Enter your nickname..."
                  className="form-input"
                  onKeyPress={(e) => e.key === "Enter" && handleNicknameSubmit()}
                />
              </div>

              {availableNicknames.length > 0 && (
                <div className="available-nicknames">
                  <p className="helper-text">Available nicknames that have voted:</p>
                  <div className="nickname-chips">
                    {availableNicknames.map((name) => (
                      <button
                        key={name}
                        onClick={() => setNickname(name)}
                        className={`nickname-chip ${nickname === name ? "selected" : ""}`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button onClick={handleClose} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleNicknameSubmit} className="btn-primary" disabled={!nickname.trim()}>
                  Next Step
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="recovery-step">
              <p className="step-description">
                To verify this is really you, please select one of the places you voted on as{" "}
                <strong>{nickname}</strong>:
              </p>

              <div className="verification-places">
                {votedPlaces.map((place) => (
                  <button
                    key={place.id}
                    onClick={() => setSelectedPlace(place.id)}
                    className={`place-option ${selectedPlace === place.id ? "selected" : ""}`}
                  >
                    <div className="place-info">
                      <span className="place-name">{place.name}</span>
                      <span className="place-vote">
                        {place.vote === "love" && "❤️ Loved"}
                        {place.vote === "like" && "👍 Liked"}
                        {place.vote === "skip" && "👎 Skipped"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button onClick={() => setStep(1)} className="btn-secondary">
                  Back
                </button>
                <button
                  onClick={handleVerification}
                  className="btn-primary"
                  disabled={!selectedPlace || isLoading}
                >
                  {isLoading ? "Recovering..." : "Recover My Votes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VoteRecoveryModal
